import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=admin
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("volmee_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("volmee_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  async function login(email, password) {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Super-admin accounts fork here — the backend returns a challenge_token
      // instead of an access_token. Surface it to the caller so LoginPage can
      // render the MFA step.
      if (data?.mfa_required) {
        return {
          ok: true,
          mfa: {
            challenge_token: data.challenge_token,
            enrolled: !!data.mfa_enrolled,
            user: data.user,
          },
        };
      }
      localStorage.setItem("volmee_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }

  async function verifyMfa(challenge_token, code) {
    try {
      const { data } = await api.post("/auth/mfa/verify", { challenge_token, code });
      if (data.recovery_codes?.length) sessionStorage.setItem("volmee_new_recovery_codes", JSON.stringify(data.recovery_codes));
      localStorage.setItem("volmee_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }

  async function enrolMfa(challenge_token) {
    try {
      const { data } = await api.post("/auth/mfa/enroll", { challenge_token });
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }

  function logout() {
    localStorage.removeItem("volmee_token");
    setUser(false);
  }

  return (
    <AuthContext.Provider value={useMemo(() => ({ user, ready, login, logout, verifyMfa, enrolMfa }), [user, ready])}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
